import type { FocusEvent, KeyboardEvent, RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { focusWhenRendered } from './focus-when-rendered';
import { useArrowKeyNet } from './use-arrow-key-net';

const VERTICAL_MOVE_KEYS: ReadonlySet<string> = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);
const ROW_ID_ATTRIBUTE = 'data-roving-row-id';

export const GRID_ROW_FOCUS_VISIBLE_CLASS_NAME =
    'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:-outline-offset-2';

type NavigableRow = {
    readonly id: string;
    readonly parentId?: string;
    readonly getCanExpand?: () => boolean;
    readonly getIsExpanded?: () => boolean;
    readonly toggleExpanded?: (expanded?: boolean) => void;
};

type NavigationContext = {
    readonly focusRow: (index: number) => void;
    readonly index: number;
    readonly open: () => void;
    readonly row: NavigableRow;
    readonly rows: readonly NavigableRow[];
};

const navigationHandlers: Readonly<Record<string, (context: NavigationContext) => boolean>> = {
    ' ': ({ open, row }) => {
        if (row.getCanExpand?.() === true) {
            row.toggleExpanded?.();
        } else {
            open();
        }

        return true;
    },
    ArrowDown: ({ focusRow, index }) => {
        focusRow(index + 1);

        return true;
    },
    ArrowLeft: ({ focusRow, row, rows }) => {
        if (row.getIsExpanded?.() === true) {
            row.toggleExpanded?.(false);

            return true;
        }

        const parentIndex = rows.findIndex((candidate) => candidate.id === row.parentId);

        if (parentIndex !== -1) {
            focusRow(parentIndex);
        }

        return true;
    },
    ArrowRight: ({ row }) => {
        if (row.getCanExpand?.() === true && row.getIsExpanded?.() !== true) {
            row.toggleExpanded?.(true);
        }

        return true;
    },
    ArrowUp: ({ focusRow, index }) => {
        focusRow(index - 1);

        return true;
    },
    End: ({ focusRow, rows }) => {
        focusRow(rows.length - 1);

        return true;
    },
    Enter: ({ open }) => {
        open();

        return true;
    },
    Home: ({ focusRow }) => {
        focusRow(0);

        return true;
    },
};

type UseTableRovingFocusOptions<TRow extends NavigableRow> = {
    readonly onOpen: (row: TRow) => void;
    readonly regionKey: string;
    readonly rows: readonly TRow[];
    readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
    readonly scrollToIndex?: (index: number) => void;
};

type RegionProps = {
    readonly 'data-focus-entry': '';
    readonly 'data-focus-region': string;
    readonly 'data-focus-region-kind': 'main';
    readonly style: { readonly outline: 'none' };
    readonly tabIndex: number;
    readonly onFocus: (event: FocusEvent<HTMLElement>) => void;
};

type RowProps = {
    readonly 'data-roving-row-id': string;
    readonly tabIndex: number;
    readonly onFocus: (event: FocusEvent<HTMLElement>) => void;
};

type TableRovingFocus = {
    readonly getRegionProps: () => RegionProps;
    readonly getRowProps: (index: number) => RowProps;
    readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
};

const rowElementForId = (container: HTMLElement | null, rowId: string): HTMLElement | null => {
    if (container === null) {
        return null;
    }

    return (
        [...container.querySelectorAll<HTMLElement>(`[${ROW_ID_ATTRIBUTE}]`)].find(
            (element) => element.getAttribute(ROW_ID_ATTRIBUTE) === rowId,
        ) ?? null
    );
};

export const useTableRovingFocus = <TRow extends NavigableRow>({
    onOpen,
    regionKey,
    rows,
    scrollContainerRef,
    scrollToIndex,
}: UseTableRovingFocusOptions<TRow>): TableRovingFocus => {
    const [focusedRowId, setFocusedRowId] = useState<string | null>(null);
    const cancelPendingFocusRef = useRef<(() => void) | null>(null);

    useEffect(
        () => () => {
            cancelPendingFocusRef.current?.();
        },
        [],
    );

    const focusRow = useCallback(
        (index: number): void => {
            const row = rows[index];

            if (row === undefined) {
                return;
            }

            setFocusedRowId(row.id);
            cancelPendingFocusRef.current?.();
            cancelPendingFocusRef.current = null;

            const rendered = rowElementForId(scrollContainerRef.current, row.id);

            if (rendered !== null) {
                scrollToIndex?.(index);
                rendered.focus();

                return;
            }

            scrollToIndex?.(index);
            cancelPendingFocusRef.current = focusWhenRendered(() =>
                rowElementForId(scrollContainerRef.current, row.id),
            );
        },
        [rows, scrollToIndex, scrollContainerRef],
    );

    const focusedIndex = focusedRowId === null ? -1 : rows.findIndex((row) => row.id === focusedRowId);
    const tabStopIndex = rows.length === 0 ? -1 : Math.max(focusedIndex, 0);

    const entryIndexForKey = useCallback(
        (key: string): number => {
            switch (key) {
                case 'Home':
                    return 0;
                case 'End':
                    return rows.length - 1;
                default:
                    return Math.max(focusedIndex, 0);
            }
        },
        [rows.length, focusedIndex],
    );

    const claimArrowKey = useCallback(
        (event: globalThis.KeyboardEvent): void => {
            if (rows.length === 0) {
                return;
            }

            const container = scrollContainerRef.current;

            if (container === null || !container.isConnected || container.offsetParent === null) {
                return;
            }

            event.preventDefault();
            focusRow(entryIndexForKey(event.key));
        },
        [rows.length, focusRow, entryIndexForKey, scrollContainerRef],
    );
    useArrowKeyNet(VERTICAL_MOVE_KEYS, claimArrowKey, scrollContainerRef);

    const armFromRegion = useCallback(
        (event: KeyboardEvent<HTMLElement>): void => {
            if (!VERTICAL_MOVE_KEYS.has(event.key)) {
                return;
            }

            focusRow(entryIndexForKey(event.key));
            event.preventDefault();
            event.stopPropagation();
        },
        [focusRow, entryIndexForKey],
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLElement>): void => {
            const target = event.target;

            if (!(target instanceof HTMLElement) || event.altKey || event.ctrlKey || event.metaKey) {
                return;
            }

            if (target === scrollContainerRef.current) {
                armFromRegion(event);

                return;
            }

            const rowId = target.getAttribute(ROW_ID_ATTRIBUTE);
            const index = rowId === null ? -1 : rows.findIndex((row) => row.id === rowId);
            const row = rows[index];

            if (row === undefined) {
                return;
            }

            const handled = navigationHandlers[event.key]?.({
                focusRow,
                index,
                open: () => onOpen(row),
                row,
                rows,
            });

            if (handled === true) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [rows, focusRow, onOpen, scrollContainerRef, armFromRegion],
    );

    const tabStopRowId = rows[tabStopIndex]?.id ?? null;
    const [isTabStopRendered, setIsTabStopRendered] = useState(true);

    // No dependency array: virtualization mounts and unmounts rows on any
    // consumer render, so the rendered state must be re-checked after each one.
    useLayoutEffect(() => {
        setIsTabStopRendered(
            tabStopRowId !== null && rowElementForId(scrollContainerRef.current, tabStopRowId) !== null,
        );
    });

    const handleRegionFocus = useCallback(
        (event: FocusEvent<HTMLElement>): void => {
            if (event.target !== event.currentTarget) {
                return;
            }

            focusRow(tabStopIndex);
        },
        [focusRow, tabStopIndex],
    );

    const getRegionProps = useCallback(
        (): RegionProps => ({
            'data-focus-entry': '',
            'data-focus-region': regionKey,
            'data-focus-region-kind': 'main',
            style: { outline: 'none' },
            tabIndex: rows.length > 0 && !isTabStopRendered ? 0 : -1,
            onFocus: handleRegionFocus,
        }),
        [regionKey, rows.length, isTabStopRendered, handleRegionFocus],
    );

    const handleRowFocus = useCallback((event: FocusEvent<HTMLElement>): void => {
        if (event.target !== event.currentTarget) {
            return;
        }

        const rowId = event.currentTarget.getAttribute(ROW_ID_ATTRIBUTE);

        if (rowId !== null) {
            setFocusedRowId(rowId);
        }
    }, []);

    const rowPropsCacheRef = useRef(new Map<number, RowProps>());

    const getRowProps = useCallback(
        (index: number): RowProps => {
            const row = rows[index];

            if (row === undefined) {
                throw new RangeError(`No navigable row at index ${index}`);
            }

            const tabIndex = index === tabStopIndex ? 0 : -1;
            const cached = rowPropsCacheRef.current.get(index);

            if (cached !== undefined && cached.tabIndex === tabIndex && cached['data-roving-row-id'] === row.id) {
                return cached;
            }

            const next: RowProps = {
                'data-roving-row-id': row.id,
                tabIndex,
                onFocus: handleRowFocus,
            };
            rowPropsCacheRef.current.set(index, next);

            return next;
        },
        [handleRowFocus, rows, tabStopIndex],
    );

    return { getRegionProps, getRowProps, onKeyDown };
};
